import {
  Box,
  Checkbox,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import TextField from 'components/text-field';
import React, { FC } from 'react';
import { TFunction } from 'i18next';

import { PartyItem } from './ron-schedule-meeting/services';
import { formatPartyRoles, partyMatchesQuery, truncatePartyName } from './ron-schedule-meeting/utils';

const useStyles = makeStyles((theme: any) => ({
  summary: {
    margin: theme.spacing(1, 0)
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0
  },
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1, 1.5, 1, 0.5),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    '&:focus-within': {
      outline: `${theme.spacing(0.25)} solid ${theme.palette.primary.main}`,
      outlineOffset: theme.spacing(0.25)
    }
  },
  cardSelected: {
    borderColor: theme.palette.primary.main
  },
  cardDisabled: {
    cursor: 'default'
  },
  checkbox: {
    flex: '0 0 auto',
    minWidth: theme.spacing(5.5),
    minHeight: theme.spacing(5.5)
  },
  text: {
    flex: '1 1 auto',
    minWidth: 0,
    paddingTop: theme.spacing(1),
    overflowWrap: 'anywhere'
  },
  name: {
    fontWeight: theme.typography.fontWeightMedium
  },
  empty: {
    padding: theme.spacing(2, 0)
  }
}));

type Props = {
  t: TFunction;
  parties: PartyItem[];
  selectedIds: Set<string>;
  filter: string;
  onFilterChange: (value: string) => void;
  onToggle: (party: PartyItem, checked: boolean) => void;
  loading: boolean;
};

const AttendeeSelection: FC<Props> = ({
  t, parties, selectedIds, filter, onFilterChange, onToggle, loading
}) => {
  const classes = useStyles();
  const visibleParties = parties.filter((party) => selectedIds.has(party._id) || partyMatchesQuery(party, filter));

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        disabled={loading}
        value={filter}
        onChange={(e) => onFilterChange(e.target.value)}
        label={t('common:filter')}
        inputProps={{ 'aria-label': t('common:filter'), autoComplete: 'off', type: 'search' }}
      />
      <Typography variant="body2" color="textSecondary" role="status" className={classes.summary}>
        {t('ron:attendees-selected', { count: selectedIds.size })}
      </Typography>
      {visibleParties.length === 0 ? (
        <Typography variant="body2" color="textSecondary" className={classes.empty}>
          {t('ron:no-parties-match')}
        </Typography>
      ) : (
        <Box component="ul" className={classes.list}>
          {visibleParties.map((party) => {
            const checked = selectedIds.has(party._id);
            const email = party.email || t('ron:not-email');
            return (
              <li key={party._id}>
                <Box
                  component="label"
                  className={clsx(classes.card, checked && classes.cardSelected, loading && classes.cardDisabled)}
                  data-testid={`party-card-${party._id}`}
                >
                  <Checkbox
                    color="primary"
                    className={classes.checkbox}
                    checked={checked}
                    disabled={loading}
                    onChange={(event) => onToggle(party, event.target.checked)}
                    inputProps={{ 'aria-label': `${party.partyName} (${email})` }}
                  />
                  <Box className={classes.text}>
                    <Typography variant="body1" color="primary" className={classes.name}>
                      {truncatePartyName(party.partyName)}
                    </Typography>
                    <Typography variant="body2">{email}</Typography>
                    <Typography variant="body2" color="textSecondary">{formatPartyRoles(party.partyKinds)}</Typography>
                  </Box>
                </Box>
              </li>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default AttendeeSelection;
